import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin, ChevronDown, Calendar } from 'lucide-react';
import { DashboardFilters as DashboardFiltersType, Location } from '../types/dashboard.types';
import { useAuth } from '@/context/AuthContext';

interface DashboardFiltersProps {
  filters: DashboardFiltersType;
  onFiltersChange: (filters: DashboardFiltersType) => void;
}

export const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  filters,
  onFiltersChange
}) => {
  const [isLocationPopoverOpen, setIsLocationPopoverOpen] = useState(false);
  const { locations: authLocations } = useAuth();

  const availableLocations: Location[] = [
    { id: 'all', name: 'Todos los Locales', active: true },
    ...authLocations.map((loc: { id: string | number; name: string }) => ({
      id: String(loc.id),
      name: loc.name,
      active: true
    }))
  ];

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value
    });
  };

  const handleLocationToggle = (locationId: string) => {
    let newLocations: string[];
    
    if (locationId === 'all') {
      // Si selecciona "Todos", limpia la selección actual y selecciona todos
      newLocations = filters.locations.includes('all') 
        ? [] 
        : availableLocations.map(loc => loc.id);
    } else {
      // Para locales específicos
      if (filters.locations.includes(locationId)) {
        // Si ya está seleccionado, lo quita
        newLocations = filters.locations.filter(id => id !== locationId && id !== 'all');
      } else {
        // Si no está seleccionado, lo agrega
        newLocations = [...filters.locations.filter(id => id !== 'all'), locationId];
        
        // Si selecciona todos los locales individuales, activa "Todos"
        const individualLocations = availableLocations.filter(loc => loc.id !== 'all');
        if (newLocations.length === individualLocations.length) {
          newLocations = ['all'];
        }
      }
    }

    onFiltersChange({
      ...filters,
      locations: newLocations
    });
  };

  const getLocationDisplayText = () => {
    if (filters.locations.includes('all') || filters.locations.length === 0) {
      return 'Todos los Locales';
    }
    if (filters.locations.length === 1) {
      const location = availableLocations.find(loc => loc.id === filters.locations[0]);
      return location?.name || 'Local seleccionado';
    }
    return `${filters.locations.length} locales seleccionados`;
  };

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          {/* Filtros de fecha */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <span className="text-sm font-medium text-gray-700 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Período:
            </span>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="startDate" className="text-xs text-gray-600">Fecha Desde</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                  className="w-auto"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="endDate" className="text-xs text-gray-600">Fecha Hasta</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleDateChange('endDate', e.target.value)}
                  className="w-auto"
                />
              </div>
            </div>
          </div>

          {/* Filtro de ubicación */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Locales:</span>
            <Popover open={isLocationPopoverOpen} onOpenChange={setIsLocationPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 min-w-[200px] justify-between"
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span className="truncate">{getLocationDisplayText()}</span>
                  </div>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-4">
                  <h4 className="font-medium text-sm text-gray-900 mb-3">
                    Seleccionar Locales
                  </h4>
                  <div className="space-y-3">
                    {availableLocations.map((location) => (
                      <div key={location.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={location.id}
                          checked={filters.locations.includes(location.id)}
                          onCheckedChange={() => handleLocationToggle(location.id)}
                        />
                        <label
                          htmlFor={location.id}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {location.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  
                  {/* Mostrar badges de selección */}
                  {filters.locations.length > 0 && !filters.locations.includes('all') && (
                    <div className="mt-4 pt-3 border-t">
                      <div className="text-xs text-gray-500 mb-2">Seleccionados:</div>
                      <div className="flex flex-wrap gap-1">
                        {filters.locations.map((locationId) => {
                          const location = availableLocations.find(loc => loc.id === locationId);
                          return location ? (
                            <Badge key={locationId} variant="secondary" className="text-xs">
                              {location.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};